/**
 * Video Optimization Utilities
 * Provides functions for optimizing video loading and performance
 */

export interface VideoPerformanceMetrics {
  loadTime: number;
  thumbnailLoadTime: number;
  iframeLoadTime: number;
  userInteractionTime?: number;
  viewportEntryTime?: number;
}

export interface VideoPreloadOptions {
  videoId: string;
  quality?: "default" | "hq" | "mq" | "sd" | "maxres";
  priority?: "high" | "low";
  preloadThumbnail?: boolean;
  preloadEmbed?: boolean;
}

// YouTube thumbnail quality options
const THUMBNAIL_QUALITIES = {
  default: "default",
  hq: "hq",
  mq: "mq",
  sd: "sd",
  maxres: "maxres",
} as const;

/**
 * Get YouTube thumbnail URL
 */
export function getYouTubeThumbnailUrl(
  videoId: string,
  quality: keyof typeof THUMBNAIL_QUALITIES = "hq",
): string {
  return `https://img.youtube.com/vi/${videoId}/${THUMBNAIL_QUALITIES[quality]}default.jpg`;
}

/**
 * Preload YouTube thumbnail
 */
export function preloadYouTubeThumbnail(
  videoId: string,
  quality: keyof typeof THUMBNAIL_QUALITIES = "hq",
): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const startTime = performance.now();

    img.onload = () => {
      const loadTime = performance.now() - startTime;
      console.log(
        `📹 Thumbnail loaded in ${loadTime.toFixed(2)}ms for video ${videoId}`,
      );
      resolve(img);
    };

    img.onerror = () => {
      reject(new Error(`Failed to load thumbnail for video ${videoId}`));
    };

    img.src = getYouTubeThumbnailUrl(videoId, quality);
  });
}

/**
 * Preload multiple YouTube thumbnails
 */
export async function preloadMultipleThumbnails(
  videoIds: string[],
  quality: keyof typeof THUMBNAIL_QUALITIES = "hq",
): Promise<void> {
  const promises = videoIds.map((videoId) =>
    preloadYouTubeThumbnail(videoId, quality).catch((error) => {
      console.warn(`Failed to preload thumbnail for ${videoId}:`, error);
    }),
  );

  await Promise.allSettled(promises);
}

/**
 * Build optimized YouTube embed URL
 */
export function buildOptimizedYouTubeUrl(
  videoId: string,
  options: {
    autoplay?: boolean;
    muted?: boolean;
    modestbranding?: boolean;
    rel?: boolean;
    showinfo?: boolean;
    iv_load_policy?: number;
    cc_load_policy?: number;
  } = {},
): string {
  const {
    autoplay = false,
    muted = false,
    modestbranding = true,
    rel = false,
    showinfo = false,
    iv_load_policy = 3,
    cc_load_policy = 0,
  } = options;

  const params = new URLSearchParams({
    rel: rel ? "1" : "0",
    modestbranding: modestbranding ? "1" : "0",
    showinfo: showinfo ? "1" : "0",
    iv_load_policy: iv_load_policy.toString(),
    cc_load_policy: cc_load_policy.toString(),
    fs: "1", // Allow fullscreen
  });

  if (autoplay) {
    params.append("autoplay", "1");
    if (muted) {
      params.append("mute", "1");
    }
  }

  return `https://www.youtube.com/embed/${videoId}?${params.toString()}`;
}

/**
 * Video performance monitor
 */
export class VideoPerformanceMonitor {
  private metrics: Map<string, VideoPerformanceMetrics> = new Map();
  private observers: Map<string, IntersectionObserver> = new Map();

  /**
   * Start monitoring a video element
   */
  startMonitoring(
    videoId: string,
    element: HTMLElement,
    options: {
      trackViewport?: boolean;
      trackInteraction?: boolean;
    } = {},
  ): void {
    const startTime = performance.now();
    const metrics: VideoPerformanceMetrics = {
      loadTime: 0,
      thumbnailLoadTime: 0,
      iframeLoadTime: 0,
    };

    this.metrics.set(videoId, metrics);

    // Track viewport entry
    if (options.trackViewport) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              metrics.viewportEntryTime = performance.now() - startTime;
              console.log(
                `📹 Video ${videoId} entered viewport at ${metrics.viewportEntryTime.toFixed(2)}ms`,
              );
              observer.disconnect();
            }
          });
        },
        { threshold: 0.1 },
      );

      observer.observe(element);
      this.observers.set(videoId, observer);
    }

    // Track user interaction
    if (options.trackInteraction) {
      const handleInteraction = () => {
        metrics.userInteractionTime = performance.now() - startTime;
        console.log(
          `📹 Video ${videoId} user interaction at ${metrics.userInteractionTime.toFixed(2)}ms`,
        );
        element.removeEventListener("click", handleInteraction);
        element.removeEventListener("play", handleInteraction);
      };

      element.addEventListener("click", handleInteraction);
      element.addEventListener("play", handleInteraction);
    }
  }

  /**
   * Record thumbnail load time
   */
  recordThumbnailLoad(videoId: string, loadTime: number): void {
    const metrics = this.metrics.get(videoId);
    if (metrics) {
      metrics.thumbnailLoadTime = loadTime;
    }
  }

  /**
   * Record iframe load time
   */
  recordIframeLoad(videoId: string, loadTime: number): void {
    const metrics = this.metrics.get(videoId);
    if (metrics) {
      metrics.iframeLoadTime = loadTime;
    }
  }

  /**
   * Get performance metrics for a video
   */
  getMetrics(videoId: string): VideoPerformanceMetrics | undefined {
    return this.metrics.get(videoId);
  }

  /**
   * Get all performance metrics
   */
  getAllMetrics(): Map<string, VideoPerformanceMetrics> {
    return new Map(this.metrics);
  }

  /**
   * Stop monitoring a video
   */
  stopMonitoring(videoId: string): void {
    const observer = this.observers.get(videoId);
    if (observer) {
      observer.disconnect();
      this.observers.delete(videoId);
    }
    this.metrics.delete(videoId);
  }

  /**
   * Stop all monitoring
   */
  stopAllMonitoring(): void {
    this.observers.forEach((observer) => observer.disconnect());
    this.observers.clear();
    this.metrics.clear();
  }
}

/**
 * Global video performance monitor instance
 */
export const videoPerformanceMonitor = new VideoPerformanceMonitor();

/**
 * Preload critical videos for better UX
 */
export async function preloadCriticalVideos(): Promise<void> {
  const criticalVideoIds = [
    "WjHWcaLFgeI", // Main homepage video
  ];

  console.log("🚀 Preloading critical videos...");

  try {
    await preloadMultipleThumbnails(criticalVideoIds, "hq");
    console.log("✅ Critical videos preloaded successfully");
  } catch (error) {
    console.warn("⚠️ Failed to preload some critical videos:", error);
  }
}

/**
 * Optimize video loading based on connection speed
 */
export function getOptimalVideoSettings(): {
  quality: keyof typeof THUMBNAIL_QUALITIES;
  preload: boolean;
  lazyLoad: boolean;
} {
  // Check connection speed if available
  if ("connection" in navigator) {
    const connection = (navigator as any).connection;

    if (
      connection.effectiveType === "slow-2g" ||
      connection.effectiveType === "2g"
    ) {
      return { quality: "default", preload: false, lazyLoad: true };
    }

    if (connection.effectiveType === "3g") {
      return { quality: "mq", preload: true, lazyLoad: true };
    }

    if (connection.effectiveType === "4g") {
      return { quality: "hq", preload: true, lazyLoad: false };
    }
  }

  // Default to good connection settings
  return { quality: "hq", preload: true, lazyLoad: false };
}

/**
 * Initialize video optimization
 */
export function initializeVideoOptimization(): void {
  // Preload critical videos
  preloadCriticalVideos();

  // Set up performance monitoring
  if (process.env.NODE_ENV === "development") {
    console.log("📹 Video performance monitoring enabled");
  }
}
